import { useEffect, useRef, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { Heart, MagnifyingGlass, Play } from '@phosphor-icons/react';
import { type Category, type Song } from '../../shared/data/mockCatalog';
import { fetchPublicScores } from '../../shared/api/memberApi';
import { drumSelectedSongAtom, drumHasDrumPassAtom, drumLanguageAtom, drumPreparationStatusAtom, drumUserPreferencesAtom } from '../../shared/store/drumAppAtoms';
import { getCategoryLabel, getSongAccess, centerInfiniteRailItem } from '../../shared/utils/drumHelpers';
import { useAutoPreview } from '../../shared/hooks/useAutoPreview';
import { useFavorites } from '../../shared/hooks/useFavorites';
import AccessLockIcon from '../../shared/components/AccessLockIcon';
import PreviewVisualizer from '../../shared/components/PreviewVisualizer';
import './DrumGame.scss';

function DrumGame() {
  const [selectedSong, setSelectedSong] = useAtom(drumSelectedSongAtom);
  const hasDrumPass = useAtomValue(drumHasDrumPassAtom);
  const language = useAtomValue(drumLanguageAtom);
  const [, setPreparationStatus] = useAtom(drumPreparationStatusAtom);
  const navigate = useNavigate();

  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');
  const { favoriteIds, toggleFavorite } = useFavorites('score');
  const [songs, setSongs] = useState<Song[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const prefs = useAtomValue(drumUserPreferencesAtom);
  const listRef = useRef<HTMLElement | null>(null);
  const { audioRef, playing } = useAutoPreview(selectedSong, prefs.autoPreview);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetchPublicScores()
      .then(({ categories: nextCategories, songs: nextSongs }) => {
        if (cancelled) return;
        setCategories(nextCategories);
        setSongs(nextSongs);
        setErrorMessage('');
        setSelectedSong((current) => current && nextSongs.some((song) => song.songId === current.songId)
          ? nextSongs.find((song) => song.songId === current.songId) || null
          : null);
      })
      .catch((error) => {
        if (cancelled) return;
        setCategories([]);
        setSongs([]);
        setSelectedSong(null);
        setErrorMessage(error instanceof Error ? error.message : (language === 'ko' ? '악보 목록을 불러오지 못했습니다.' : 'Unable to load songs.'));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [language, setSelectedSong]);

  const visibleSongs = songs
    .filter((song) => song.publishStatus === 'public')
    .sort((first, second) => new Date(second.registeredAt).getTime() - new Date(first.registeredAt).getTime());

  const list = visibleSongs.filter((song) => {
    const matchesCategory = category === 'all' || (category === 'favorites' ? favoriteIds.includes(song.songId) : song.categoryId === category);
    return matchesCategory && `${song.title} ${song.artist}`.toLowerCase().includes(query.toLowerCase());
  });

  const categoryItems = [
    { categoryId: 'favorites', nameKo: '즐겨찾기', nameEn: 'Favorites' },
    { categoryId: 'all', nameKo: '전체', nameEn: 'All' },
    ...categories.filter((item) => visibleSongs.some((song) => song.categoryId === item.categoryId)),
  ];

  const selectedFavorite = selectedSong ? favoriteIds.includes(selectedSong.songId) : false;
  const emptyCopy = errorMessage
    || (isLoading
      ? (language === 'ko' ? '곡을 불러오는 중...' : 'Loading songs...')
      : category === 'favorites'
        ? (language === 'ko' ? '즐겨찾기한 곡이 없습니다.' : 'No favorite songs yet.')
        : (language === 'ko' ? '곡이 없습니다.' : 'No songs found.'));

  const selectCategory = (nextCategory: string) => {
    setCategory(nextCategory);
    setSelectedSong(null);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
      listRef.current.scrollLeft = 0;
    }
  };

  const selectSong = (song: Song, event: React.MouseEvent<HTMLButtonElement>) => {
    const target = event.currentTarget;
    setSelectedSong(song);
    window.requestAnimationFrame(() => centerInfiniteRailItem(listRef.current, target));
  };

  const beginPreparation = () => {
    if (!selectedSong) return;
    setPreparationStatus('loading');
    navigate('/drum/preparation');
  };

  return (
    <div className="page drumgame-page">
      {selectedSong && <audio key={selectedSong.songId} ref={audioRef} src={selectedSong.previewAudio} preload="auto" data-preview-audio />}
      <div className="drumgame-tools">
        <div className="category-rail">
          {categoryItems.map((item) => (
            <button className={category === item.categoryId ? 'active' : ''} key={item.categoryId} onClick={() => selectCategory(item.categoryId)}>
              {item.categoryId === 'favorites' && <Heart weight={category === 'favorites' ? 'fill' : 'regular'} />}
              {language === 'ko' ? item.nameKo : item.nameEn}
            </button>
          ))}
        </div>
        <label className="search-box"><MagnifyingGlass /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search songs or artists" /></label>
      </div>
      <div className="drumgame-grid">
        <div className="scroll-rail-shell">
          <aside className="song-list" ref={listRef as React.RefObject<HTMLElement>}>
            {list.length ? list.map((song, index) => {
              const access = getSongAccess(song, hasDrumPass, language);
              return (
                <button key={song.songId} data-song-id={song.songId} className={`song-row ${selectedSong?.songId === song.songId ? 'selected' : ''}`} onClick={(event) => selectSong(song, event)}>
                  <span className="song-rank">{String(index + 1).padStart(2, '0')}</span>
                  {song.thumbnail ? <img src={song.thumbnail} alt="" /> : <span className="song-thumb-fallback" aria-hidden="true" />}
                  <span className="song-copy"><strong>{song.title}</strong><small>{song.artist}</small></span>
                  <span className="song-side">
                    <em className={`song-access song-access--${access.type}`} title={access.label}><AccessLockIcon type={access.type} />{access.shortLabel}</em>
                    {song.bpm > 0 && <span className="song-bpm">{song.bpm}<small>BPM</small></span>}
                  </span>
                </button>
              );
            }) : <div className="empty-list">{emptyCopy}</div>}
          </aside>
        </div>
        {selectedSong ? (
          <section className="song-selection-summary">
            <div className="selection-cover">
              {selectedSong.thumbnail ? <img src={selectedSong.thumbnail} alt={`${selectedSong.title} cover`} /> : <span className="song-thumb-fallback" aria-hidden="true" />}
              {selectedSong.previewAudio && <PreviewVisualizer playing={playing} className="preview-live" />}
              <button className={`favorite-button selection-favorite ${selectedFavorite ? 'active' : ''}`} onClick={() => toggleFavorite(selectedSong.songId)} aria-label="Favorite" aria-pressed={selectedFavorite}><Heart weight={selectedFavorite ? 'fill' : 'regular'} /></button>
            </div>
            <div className="selection-copy">
              <span>{[getCategoryLabel(selectedSong.categoryId, language, categories), selectedSong.bpm > 0 ? `${selectedSong.bpm} BPM` : null].filter(Boolean).join(' · ')}</span>
              <h1>{selectedSong.title}</h1>
              <p>{selectedSong.artist}</p>
            </div>
            <button type="button" className="song-play-button" onClick={beginPreparation}><Play weight="fill" /><strong>{language === 'ko' ? '드럼 플레이' : 'PLAY DRUMS'}</strong></button>
          </section>
        ) : (
          <section className="song-selection-summary song-selection-empty">
            <strong>{language === 'ko' ? '연주할 곡을 선택하세요' : 'Select a song to play'}</strong>
            <small>{language === 'ko' ? '곡을 선택하면 20초 미리듣기가 시작됩니다.' : 'Select a song to hear a 20-second preview.'}</small>
          </section>
        )}
      </div>
    </div>
  );
}

export default DrumGame;
